import {
  Computer,
  Devices,
  Email,
  Forum,
  Gavel,
  Logout,
  ManageAccounts,
  PhoneIphone,
  Refresh,
  Smartphone,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import md5 from 'md5';
import React, { useCallback, useEffect, useState } from 'react';
import { CustomEditor } from '../core/CustomEditor';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import { customFetcher } from '../hooks/useFetch';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { prettyClientName } from '../core/clientNames';
import { AccountNotifications } from './AccountNotifications';

interface UserSelf {
  idUser: number;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  Signature?: string | null;
}

interface SessionInfo {
  idUserSession: number;
  ClientName: string;
  IP?: string | null;
  UserAgent?: string | null;
  Created_at?: string;
  LastUsed_at?: string;
  Expires_at?: string | null;
  isCurrent: boolean;
}

const clientIcon = (clientName: string) => {
  const lower = (clientName || '').toLowerCase();
  if (lower.includes('thunderbird')) return <Forum fontSize='small' />;
  if (lower.includes('outlook')) return <Email fontSize='small' />;
  if (lower.includes('word')) return <Gavel fontSize='small' />;
  if (lower.includes('cli') || lower.includes('agent'))
    return <Computer fontSize='small' />;
  if (lower.includes('mitarbeiter-app')) return <Smartphone fontSize='small' />;
  if (lower.includes('mobile') || lower.includes('phone'))
    return <PhoneIphone fontSize='small' />;
  return <Devices fontSize='small' />;
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
};

const minimalUserAgent = (ua?: string | null): string => {
  if (!ua) return '';
  // Saubere Kurzform aus dem User-Agent: Browser/Plugin + OS
  const browser = (() => {
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Edg\//i.test(ua)) return 'Edge';
    if (/Chrome\//i.test(ua)) return 'Chrome';
    if (/Safari\//i.test(ua)) return 'Safari';
    if (/Thunderbird/i.test(ua)) return 'Thunderbird';
    return ua.split(' ')[0];
  })();
  const os = (() => {
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    if (/Android/i.test(ua)) return 'Android';
    if (/iOS|iPhone|iPad/i.test(ua)) return 'iOS';
    return '';
  })();
  return [browser, os].filter(Boolean).join(' · ');
};

export const AccountView: React.FC = () => {
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');

  const [user, setUser] = useState<UserSelf | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSignature, setEditSignature] = useState('');
  const [busyProfile, setBusyProfile] = useState(false);

  // Live-Check der Email-Verfuegbarkeit fuer Admins.
  const [emailCheck, setEmailCheck] = useState<{
    state: 'idle' | 'checking' | 'available' | 'taken';
    usedBy?: string;
  }>({ state: 'idle' });

  // E-Mail-Aenderung ist Admin-only. localStorage.role wird beim Login von
  // SecurityDriver gesetzt — gleiches Pattern wie z. B. in
  // LegalTransactionMeetingOverview.tsx.
  const isAdmin = localStorage.getItem('role') === 'Administrator';

  // Debounced live-check, ob die eingetragene Email noch frei ist.
  useEffect(() => {
    if (!isAdmin) {
      setEmailCheck({ state: 'idle' });
      return;
    }
    const value = editEmail.trim();
    if (!value || value === (user?.Email || '').trim()) {
      setEmailCheck({ state: 'idle' });
      return;
    }
    setEmailCheck({ state: 'checking' });
    const handle = window.setTimeout(async () => {
      try {
        const res = (await customFetcher({
          endpoint: `/account/check-email?email=${encodeURIComponent(value)}`,
        })) as { available: boolean; usedBy: string | null };
        if (res.available) {
          setEmailCheck({ state: 'available' });
        } else {
          setEmailCheck({
            state: 'taken',
            usedBy: res.usedBy || 'ein anderer Benutzer',
          });
        }
      } catch {
        // Im Zweifel als verfuegbar markieren — die finale Pruefung passiert
        // serverseitig beim Speichern.
        setEmailCheck({ state: 'idle' });
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [editEmail, isAdmin, user?.Email]);

  const [pwNew, setPwNew] = useState('');
  const [pwRepeat, setPwRepeat] = useState('');
  const [busyPassword, setBusyPassword] = useState(false);
  const [busyResetMail, setBusyResetMail] = useState(false);

  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [busySession, setBusySession] = useState<number | null>(null);
  const [busyRevokeOthers, setBusyRevokeOthers] = useState(false);
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  // === User-Daten laden
  const loadUser = useCallback(async () => {
    setIsLoadingUser(true);
    try {
      const data = (await customFetcher({
        endpoint: '/account',
      })) as UserSelf;
      setUser(data);
      setEditFirstName(data.FirstName || '');
      setEditLastName(data.LastName || '');
      setEditEmail(data.Email || '');
      setEditSignature(data.Signature || '');
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? e.message
            : 'Profil konnte nicht geladen werden.',
      });
    } finally {
      setIsLoadingUser(false);
    }
  }, [showAlert]);

  // === Sessions laden
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const data = (await customFetcher({
        endpoint: '/sessions',
      })) as SessionInfo[];
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? e.message
            : 'Sessions konnten nicht geladen werden.',
      });
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, [showAlert]);

  useEffect(() => {
    void loadUser();
    void loadSessions();
  }, [loadUser, loadSessions]);

  // === Profil speichern
  const saveProfile = useCallback(async () => {
    if (!user) return;
    if (!editFirstName.trim() || !editLastName.trim()) {
      showAlert({
        severity: 'error',
        text: 'Vor- und Nachname dürfen nicht leer sein.',
      });
      return;
    }
    setBusyProfile(true);
    try {
      const body: Record<string, string> = {
        FirstName: editFirstName.trim(),
        LastName: editLastName.trim(),
        Signature: editSignature,
      };
      // E-Mail nur senden, wenn der User Admin ist und etwas geaendert hat.
      // Der Backend-Endpoint ignoriert Email-Eintraege von Non-Admins
      // ohnehin, das hier ist Belt-and-Suspenders.
      if (isAdmin) {
        body.Email = editEmail.trim();
      }
      const updated = await customFetcher({
        endpoint: '/account/profile',
        method: 'PUT',
        body,
      });
      setUser(updated as UserSelf);
      // setEditEmail synchronisieren, damit Backend-Trim sichtbar wird
      if (updated && typeof (updated as UserSelf).Email === 'string') {
        setEditEmail((updated as UserSelf).Email || '');
      }
      showAlert({ text: 'Profil aktualisiert.' });
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? e.message
            : 'Profil konnte nicht gespeichert werden.',
      });
    } finally {
      setBusyProfile(false);
    }
  }, [
    editFirstName,
    editLastName,
    editEmail,
    editSignature,
    isAdmin,
    showAlert,
    user,
  ]);

  // === Passwort ändern
  const savePassword = useCallback(async () => {
    if (!user) return;
    if (pwNew.length < 12) {
      showAlert({
        severity: 'error',
        text: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
      });
      return;
    }
    if (pwNew !== pwRepeat) {
      showAlert({
        severity: 'error',
        text: 'Die Passwörter stimmen nicht überein.',
      });
      return;
    }
    setBusyPassword(true);
    try {
      await customFetcher({
        endpoint: '/account/password',
        method: 'POST',
        body: { Password: md5(pwNew) },
      });
      setPwNew('');
      setPwRepeat('');
      showAlert({ text: 'Passwort geändert.' });
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? e.message
            : 'Passwort konnte nicht geändert werden.',
      });
    } finally {
      setBusyPassword(false);
    }
  }, [pwNew, pwRepeat, showAlert, user]);

  const requestPasswordResetMail = useCallback(async () => {
    if (!user) return;
    if (
      !window.confirm(
        'Es wird ein neues Passwort generiert und an Ihre E-Mail-Adresse gesendet. Fortfahren?'
      )
    ) {
      return;
    }
    setBusyResetMail(true);
    try {
      await customFetcher({
        endpoint: '/account/password-reset-mail',
        method: 'POST',
      });
      showAlert({ text: 'Neues Passwort wurde per E-Mail versendet.' });
    } catch (e) {
      showAlert({
        severity: 'error',
        text: e instanceof Error ? e.message : 'Passwort-Reset fehlgeschlagen.',
      });
    } finally {
      setBusyResetMail(false);
    }
  }, [showAlert, user]);

  // === Session-Aktionen
  const handleLocalLogout = useCallback(() => {
    // Nach „Alle beenden" oder Self-Revoke: lokalen Token entfernen, zur
    // Login-Seite. Die Backend-Session ist bereits gelöscht, ein weiterer
    // API-Call würde 401 ergeben.
    localStorage.removeItem('SessionToken');
    localStorage.removeItem('idUser');
    localStorage.removeItem('role');
    localStorage.removeItem('scope');
    navigate('/');
    window.location.reload();
  }, [navigate]);

  const revokeOne = useCallback(
    async (s: SessionInfo) => {
      if (
        !window.confirm(
          s.isCurrent
            ? 'Dies ist Ihre aktuelle Session. Nach dem Beenden werden Sie abgemeldet. Fortfahren?'
            : `Session „${prettyClientName(s.ClientName)}" wirklich beenden?`
        )
      ) {
        return;
      }
      setBusySession(s.idUserSession);
      try {
        const res = (await customFetcher({
          endpoint: `/sessions/${s.idUserSession}`,
          method: 'DELETE',
        })) as { wasCurrent?: boolean };
        if (res?.wasCurrent || s.isCurrent) {
          handleLocalLogout();
          return;
        }
        await loadSessions();
        showAlert({ text: 'Session beendet.' });
      } catch (e) {
        showAlert({
          severity: 'error',
          text:
            e instanceof Error
              ? e.message
              : 'Session konnte nicht beendet werden.',
        });
      } finally {
        setBusySession(null);
      }
    },
    [handleLocalLogout, loadSessions, showAlert]
  );

  const revokeOthers = useCallback(async () => {
    setBusyRevokeOthers(true);
    try {
      await customFetcher({
        endpoint: '/sessions/revoke-others',
        method: 'POST',
      });
      await loadSessions();
      showAlert({ text: 'Alle anderen Sessions wurden beendet.' });
    } catch (e) {
      showAlert({
        severity: 'error',
        text: e instanceof Error ? e.message : 'Aktion fehlgeschlagen.',
      });
    } finally {
      setBusyRevokeOthers(false);
    }
  }, [loadSessions, showAlert]);

  const revokeAllConfirmed = useCallback(async () => {
    setConfirmRevokeAll(false);
    try {
      await customFetcher({ endpoint: '/sessions/revoke-all', method: 'POST' });
    } catch {
      /* Token ist evtl. schon weg; egal, lokal abmelden. */
    }
    handleLocalLogout();
  }, [handleLocalLogout]);

  if (isLoadingUser) return <LoadingSpinner height='60vh' />;
  if (!user) {
    return <Alert severity='error'>Profil konnte nicht geladen werden.</Alert>;
  }

  const otherSessionsCount = sessions.filter(s => !s.isCurrent).length;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
        <ManageAccounts color='primary' />
        <Typography variant='h5'>Mein Konto</Typography>
      </Stack>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <TabList onChange={(_, v: string) => setTab(v)}>
            <Tab label='Profil' value='profile' />
            <Tab label='Passwort' value='password' />
            <Tab
              label={`Sessions${sessions.length ? ` (${sessions.length})` : ''}`}
              value='sessions'
            />
            <Tab label='Benachrichtigungen' value='notifications' />
          </TabList>
        </Box>

        <TabPanel value='profile' sx={{ p: 0 }}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 2 }}>
                Persönliche Daten
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label='Vorname'
                    fullWidth
                    value={editFirstName}
                    onChange={e => setEditFirstName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label='Nachname'
                    fullWidth
                    value={editLastName}
                    onChange={e => setEditLastName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='E-Mail'
                    fullWidth
                    value={isAdmin ? editEmail : user.Email || ''}
                    onChange={e => setEditEmail(e.target.value)}
                    disabled={!isAdmin}
                    type='email'
                    error={isAdmin && emailCheck.state === 'taken'}
                    helperText={
                      !isAdmin
                        ? 'Die E-Mail-Adresse wird vom Administrator gepflegt.'
                        : emailCheck.state === 'checking'
                          ? 'Prüfe Verfügbarkeit…'
                          : emailCheck.state === 'taken'
                            ? `Wird bereits von ${emailCheck.usedBy} verwendet.`
                            : emailCheck.state === 'available'
                              ? 'E-Mail-Adresse ist verfügbar.'
                              : ' '
                    }
                  />
                </Grid>
              </Grid>

              <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
                Signatur
              </Typography>
              <CustomEditor
                idTextModuleCategory={1}
                dictonaryIdsArray={[]}
                txt={editSignature}
                setTxt={(txt: string) => setEditSignature(txt)}
              />

              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Button
                  variant='outlined'
                  onClick={() => {
                    setEditFirstName(user.FirstName || '');
                    setEditLastName(user.LastName || '');
                    setEditEmail(user.Email || '');
                    setEditSignature(user.Signature || '');
                  }}
                  disabled={busyProfile}
                >
                  Abbruch
                </Button>
                <Button
                  variant='contained'
                  onClick={saveProfile}
                  disabled={
                    busyProfile || (isAdmin && emailCheck.state === 'taken')
                  }
                >
                  {busyProfile ? <CircularProgress size={18} /> : 'Speichern'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value='password' sx={{ p: 0 }}>
          <Card variant='outlined' sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 2 }}>
                Passwort ändern
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type='password'
                    label='Neues Passwort'
                    fullWidth
                    value={pwNew}
                    onChange={e => setPwNew(e.target.value)}
                    autoComplete='new-password'
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type='password'
                    label='Passwort wiederholen'
                    fullWidth
                    value={pwRepeat}
                    onChange={e => setPwRepeat(e.target.value)}
                    autoComplete='new-password'
                  />
                </Grid>
              </Grid>
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
                sx={{ mt: 1 }}
              >
                Mindestens 12 Zeichen. Beide Eingaben müssen übereinstimmen.
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant='contained'
                  onClick={savePassword}
                  disabled={busyPassword || !pwNew || !pwRepeat}
                >
                  {busyPassword ? (
                    <CircularProgress size={18} />
                  ) : (
                    'Passwort speichern'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card variant='outlined'>
            <CardContent>
              <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
                Passwort per E-Mail anfordern
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Sie erhalten ein neu generiertes Passwort an Ihre hinterlegte
                E-Mail-Adresse <b>{user.Email || '–'}</b>. Das alte Passwort
                wird damit ungültig.
              </Typography>
              <Button
                variant='outlined'
                startIcon={<Email />}
                onClick={requestPasswordResetMail}
                disabled={busyResetMail || !user.Email}
              >
                {busyResetMail
                  ? 'Anfrage läuft…'
                  : 'Neues Passwort per E-Mail senden'}
              </Button>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value='sessions' sx={{ p: 0 }}>
          <Card variant='outlined'>
            <CardContent>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                flexWrap='wrap'
                gap={1}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant='subtitle1' fontWeight={600}>
                    Aktive Sessions und autorisierte Plugins
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Eine Übersicht aller Geräte, auf denen Sie aktuell mit DiNo
                    verbunden sind.
                  </Typography>
                </Box>
                <Stack direction='row' spacing={1} flexWrap='wrap' rowGap={1}>
                  <Button
                    variant='outlined'
                    startIcon={<Smartphone />}
                    // Bewusst dieselbe Seite wie der Device-Flow der Plugins:
                    // Codes eingeben, Zugriff erlauben und die Serveradresse
                    // als QR-Code liegen an einer Stelle statt in zwei fast
                    // gleichen Masken. `from=account` fuehrt danach hierher
                    // zurueck, damit die Geraeteliste gleich neu geladen wird.
                    onClick={() => navigate('/authorize?from=account')}
                  >
                    Gerät verbinden
                  </Button>
                  <Tooltip title='Liste aktualisieren'>
                    <span>
                      <IconButton
                        onClick={() => void loadSessions()}
                        disabled={isLoadingSessions}
                      >
                        {isLoadingSessions ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Refresh />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    variant='outlined'
                    onClick={revokeOthers}
                    disabled={otherSessionsCount === 0 || busyRevokeOthers}
                  >
                    {busyRevokeOthers
                      ? 'Beende…'
                      : `Andere Sessions beenden (${otherSessionsCount})`}
                  </Button>
                  <Button
                    color='error'
                    variant='contained'
                    startIcon={<Logout />}
                    onClick={() => setConfirmRevokeAll(true)}
                    disabled={sessions.length === 0}
                  >
                    Alle beenden
                  </Button>
                </Stack>
              </Stack>

              {isLoadingSessions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : sessions.length === 0 ? (
                <Alert severity='info'>Keine aktiven Sessions.</Alert>
              ) : (
                <List dense disablePadding>
                  {sessions.map((s, idx) => (
                    <React.Fragment key={s.idUserSession}>
                      {idx > 0 && <Divider component='li' />}
                      <ListItem alignItems='flex-start' sx={{ py: 1.5 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: s.isCurrent
                                ? 'primary.main'
                                : 'action.selected',
                              color: s.isCurrent
                                ? 'primary.contrastText'
                                : 'text.primary',
                            }}
                          >
                            {clientIcon(s.ClientName)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                              flexWrap='wrap'
                            >
                              <Typography variant='body1' fontWeight={600}>
                                {prettyClientName(s.ClientName)}
                              </Typography>
                              {s.isCurrent && (
                                <Chip
                                  size='small'
                                  color='primary'
                                  label='Diese Session'
                                />
                              )}
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={0.25}>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {minimalUserAgent(s.UserAgent) ||
                                  s.UserAgent ||
                                  ''}
                                {s.IP ? ` · ${s.IP}` : ''}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                Angelegt: {formatDate(s.Created_at)} · Zuletzt
                                aktiv: {formatDate(s.LastUsed_at)}
                              </Typography>
                            </Stack>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size='small'
                            color={s.isCurrent ? 'warning' : 'error'}
                            onClick={() => void revokeOne(s)}
                            disabled={busySession === s.idUserSession}
                          >
                            {busySession === s.idUserSession
                              ? 'Beende…'
                              : s.isCurrent
                                ? 'Hier abmelden'
                                : 'Beenden'}
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value='notifications' sx={{ p: 0 }}>
          <AccountNotifications />
        </TabPanel>
      </TabContext>

      {/* Bestätigung „Alle beenden" — destruktiv, daher Extra-Modal */}
      <Dialog
        open={confirmRevokeAll}
        onClose={() => setConfirmRevokeAll(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Warning color='error' />
            <span>Alle Sessions beenden?</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sie melden alle Geräte und autorisierten Plugins ab — auch Ihre
            aktuelle Session in diesem Browser. Danach müssen Sie sich erneut
            anmelden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevokeAll(false)}>Abbruch</Button>
          <Button
            color='error'
            variant='contained'
            onClick={() => void revokeAllConfirmed()}
          >
            Alle beenden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
